import { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Bell, Search, Plus } from 'lucide-react';
import { Button } from './ui/Button';
import { Breadcrumb } from './ui/Breadcrumb';
import { PermissionGate } from './PermissionGate';
import { NotificationsCenter } from './NotificationsCenter';
import { useNotifications } from '@/hooks/useNotifications';
import { useAuth } from '@/contexts/AuthContext';

export function LayoutWithSidebar() {
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { unreadCount } = useNotifications();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/contracts?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-background border-b border-input shadow-sm">
          <div className="px-6 py-4 flex items-center justify-between">
            {/* Search */}
            <div className="flex-1 max-w-2xl">
              <div className="relative">
                <button
                  onClick={handleSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  title="Buscar"
                >
                  <Search className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  placeholder="Buscar contratos... (título, contratante, contratado)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-10 pr-4 py-2.5 bg-muted border border-input rounded-sm text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 ml-6">
              <PermissionGate permission="create_contract">
                <Link to="/contracts/new">
                  <Button size="sm" className="shadow-lg shadow-blue-500/20">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Contrato
                  </Button>
                </Link>
              </PermissionGate>

              {/* Notifications */}
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-sm transition-all"
                title={unreadCount > 0 ? `${unreadCount} notificações não lidas` : 'Notificações'}
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] bg-destructive/100 text-white text-xs font-bold rounded-full px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="px-6 pt-4">
          <Breadcrumb />
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="bg-background border-t border-input px-6 py-3">
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <span>Dúvidas e informações: <a href="mailto:ti@fiberx.com.br" className="text-primary hover:underline">ti@fiberx.com.br</a></span>
              <span className="hidden sm:inline text-muted-foreground">•</span>
              <span>FiberX Contratos © 2026 • Desenvolvido pela TI com 💙</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/user-guide" className="hover:text-foreground transition-colors">
                Manual do Usuário
              </Link>
              {isAdmin && (
                <>
                  <span className="text-muted-foreground">•</span>
                  <Link to="/system-check" className="hover:text-foreground transition-colors">
                    Diagnóstico do Sistema
                  </Link>
                </>
              )}
            </div>
          </div>
        </footer>
      </div>

      {/* Notifications Panel */}
      <NotificationsCenter
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
}
